import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import { getConfig } from '@leagueplatform/core';

// ChatHub serves the tenant's demo kits with each kit's persona cast already
// resolved, so one call fills both selects. Only deployed, deep-cloned kits are
// listed: the seeded golden kit is the template a demo is cloned from, not a demo.
const DEMO_KITS_PATH = '/v1/agentic-demo-kits';

type DemoPersona = {
  id: string;
  name: string;
};

export type DemoKit = {
  bundleId: string;
  name: string;
  personas: DemoPersona[];
};

type DemoKitsResponse = {
  data?: {
    attributes?: {
      bundleId?: string;
      name?: string;
      personas?: { id?: string; name?: string }[];
    };
  }[];
};

// Ids are read from this response every time rather than remembered: a cast is
// recreated on every re-seed, and a kit id is minted per clone.
export const fetchDemoKits = async (): Promise<DemoKit[]> => {
  const token = await StandaloneAuth.client.getTokenSilently();
  const response = await fetch(
    `${getConfig().config.core.api.url}${DEMO_KITS_PATH}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Demo kits request failed with ${response.status}`);
  }

  const body: DemoKitsResponse = await response.json();

  const kits = (body.data ?? []).flatMap(({ attributes }) => {
    if (!attributes?.bundleId) return [];

    return [
      {
        bundleId: attributes.bundleId,
        name: attributes.name ?? attributes.bundleId,
        personas: (attributes.personas ?? []).flatMap(({ id, name }) =>
          id ? [{ id, name: name ?? id }] : [],
        ),
      },
    ];
  });

  return kits;
};
